import type { Dosage, MedicationStatement } from "@medplum/fhirtypes";
import {
  getTRPCClient,
  MEDICATION_STATEMENT_ENTITY_ID_SYSTEM,
  medicationDosageFromFhir,
  medicationDosageToFhir,
} from "@repo/api";
import {
  type MedicationIdInput,
  medicationIdInputSchema,
  medicationStatementIdSchema,
  patientIdSchema,
  type RecordMedicationInput,
  recordMedicationInputSchema,
  type UpdateMedicationInput,
  updateMedicationInputSchema,
} from "@repo/api/schemas";
import type { MedicationDosage, MedicationStatementId, PatientId } from "@repo/domain";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { MedicationStatementRow } from "../fhir/types";
import { optimisticPatientReference, patientDomainId } from "./_patient-reference";
import type { CodeableConcept, Period } from "./shared/types";

/**
 * MedicationStatement — normalized read-model for "what the patient is taking".
 * Read-only for now; the record/stop flow arrives with the medication domain.
 *
 * `medication` flattens `medicationCodeableConcept`. When a statement instead
 * references a `Medication` resource we surface its id in `medicationId` (the
 * caller resolves the display) — seeds use the inline CodeableConcept form.
 */
export interface MedicationStatementDto {
  medicationStatementId: MedicationStatementId;
  patientId: PatientId;

  status: "active" | "completed" | "entered-in-error" | "intended" | "stopped" | "on-hold" | "unknown" | "not-taken";

  /** Inline medication code (`medicationCodeableConcept`). */
  medication?: CodeableConcept;
  /** Referenced Medication id (`medicationReference`) when not inline. */
  medicationId?: string;

  /** First dosage instruction's free-text (`dosage[0].text`). */
  dosageText?: string;
  /** Amount, frequency, route and as-needed read off `dosage[0]`. */
  dosage?: MedicationDosage;

  effectiveDateTime?: string;
  effectivePeriod?: Period;
}

const COLLECTION = "medicationStatement";

const VALID_STATUS: ReadonlySet<string> = new Set([
  "active",
  "completed",
  "entered-in-error",
  "intended",
  "stopped",
  "on-hold",
  "unknown",
  "not-taken",
]);

function flatCodeable(
  c: { coding?: Array<{ system?: string; code?: string; display?: string }>; text?: string } | undefined,
): CodeableConcept | undefined {
  if (!c) return undefined;
  const first = c.coding?.[0];
  const out = {
    ...(first?.system ? { system: first.system } : {}),
    ...(first?.code ? { code: first.code } : {}),
    ...(first?.display ? { display: first.display } : {}),
    ...(c.text ? { text: c.text } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

function flatPeriod(p: { start?: string; end?: string } | undefined): Period | undefined {
  if (!p) return undefined;
  const out = {
    ...(p.start ? { start: p.start } : {}),
    ...(p.end ? { end: p.end } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

function refId(ref: string | undefined, type: string): string | undefined {
  if (!ref?.startsWith(`${type}/`)) return undefined;
  return ref.slice(type.length + 1);
}

function toDto(m: MedicationStatementRow): MedicationStatementDto | undefined {
  if (!m.domainId) return undefined;
  const medicationStatementId = medicationStatementIdSchema.safeParse(m.domainId);
  const patientId = patientIdSchema.safeParse(patientDomainId(m.subject));
  if (!medicationStatementId.success || !patientId.success) return undefined;
  const status = m.status && VALID_STATUS.has(m.status) ? m.status : "unknown";
  const medication = flatCodeable(m.medicationCodeableConcept);
  const dosageText = m.dosage?.find((d) => d.text)?.text;
  const dosage = medicationDosageFromFhir(m.dosage?.[0]);
  return {
    medicationStatementId: medicationStatementId.data,
    patientId: patientId.data,
    status: status as MedicationStatementDto["status"],
    ...(medication ? { medication } : {}),
    ...(refId(m.medicationReference?.reference, "Medication")
      ? { medicationId: refId(m.medicationReference?.reference, "Medication") }
      : {}),
    ...(dosageText ? { dosageText } : {}),
    ...(dosage ? { dosage } : {}),
    ...(m.effectiveDateTime ? { effectiveDateTime: m.effectiveDateTime } : {}),
    ...(flatPeriod(m.effectivePeriod) ? { effectivePeriod: flatPeriod(m.effectivePeriod) } : {}),
  };
}

interface UseMedicationStatementsParams {
  patientId?: string;
}

interface UseMedicationStatementsResult {
  medications: MedicationStatementDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

export function useMedicationStatements(params?: UseMedicationStatementsParams): UseMedicationStatementsResult {
  const collection = useCollection(COLLECTION);
  const patientId = params?.patientId;

  const result = useLiveQuery({
    queryKey: [collection.id, "medication-statements", patientId ?? null],
    query: (q) => {
      if (patientId) {
        return q
          .from({ m: collection })
          .where(({ m }) => eq(m.subject?.identifier?.value, patientId))
          .fn.select(({ m }) => ({ dto: toDto(m) }));
      }
      return undefined;
    },
  });

  return {
    medications: ((result.data as Array<{ dto?: MedicationStatementDto }> | undefined) ?? []).flatMap((row) =>
      row.dto ? [row.dto] : [],
    ),
    isLoading: result.isLoading,
    isReady: result.isReady,
    isError: result.isError,
  };
}

// ── Write-side intents (optimistic) ──

function dosageArray(first: Dosage | undefined, rest: Dosage[] = []): Dosage[] | undefined {
  const all = first ? [first, ...rest] : rest;
  return all.length ? all : undefined;
}

const medications = () => getDataStoreClient().getCollection<MedicationStatementRow, string>(COLLECTION);

/** Rows are keyed by domain identity; this only asserts the row is loaded. */
function resourceIdForDomainId(statementId: MedicationStatementId): string {
  if (!medications().has(statementId)) throw new Error(`Medication statement '${statementId}' is not loaded`);
  return statementId;
}

/** Intent: "record a medication the patient is taking". */
export const recordMedication = defineWrite<RecordMedicationInput>({
  optimistic: (params) => {
    const input = recordMedicationInputSchema.parse(params);
    const optimisticStatementId = input.id;
    const record = {
      resourceType: "MedicationStatement",
      identifier: [{ system: MEDICATION_STATEMENT_ENTITY_ID_SYSTEM, value: optimisticStatementId }],
      domainId: optimisticStatementId,
      status: input.status,
      medicationCodeableConcept: input.medication,
      subject: optimisticPatientReference(input.patient.id),
      effectiveDateTime: input.effectiveDateTime,
      effectivePeriod: input.effectivePeriod,
      dosage: dosageArray(medicationDosageToFhir(input.dosage, input.dosageText, undefined)),
      dateAsserted: input.dateAsserted ?? new Date().toISOString(),
    } as MedicationStatementRow;
    medications().insert(record);
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.medication.record.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "stop a medication" — status→stopped. */
export const stopMedication = defineWrite<MedicationIdInput>({
  optimistic: (params) => {
    const input = medicationIdInputSchema.parse(params);
    const collection = medications();
    const resourceId = resourceIdForDomainId(input.medicationStatementId);
    collection.update(resourceId, (draft) => {
      draft.status = "stopped";
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.medication.stop.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "update a medication's dosage or effective timing". */
export const updateMedication = defineWrite<UpdateMedicationInput>({
  optimistic: (params) => {
    const input = updateMedicationInputSchema.parse(params);
    const collection = medications();
    const resourceId = resourceIdForDomainId(input.medicationStatementId);
    const changes: Partial<MedicationStatement> = {};
    if (input.dosageText !== undefined || input.dosage !== undefined) {
      // An omitted half is unchanged, so it is read back off the row before the merge.
      const [first, ...rest] = collection.get(resourceId)?.dosage ?? [];
      const text = input.dosageText !== undefined ? input.dosageText || undefined : first?.text;
      const structured = input.dosage ?? medicationDosageFromFhir(first);
      const next = medicationDosageToFhir(structured, text, first);
      changes.dosage = dosageArray(next, rest);
    }
    if (input.effectiveDateTime !== undefined) changes.effectiveDateTime = input.effectiveDateTime;
    if (input.effectivePeriod !== undefined) changes.effectivePeriod = input.effectivePeriod;
    collection.update(resourceId, (draft) => {
      Object.assign(draft, changes);
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.medication.update.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});
